import React, { useEffect, useState } from 'react';
import PreviewCard from './PreviewCard';

async function fetchPreviewForUrl(url, title, fallbackThumbnail) {
    if (!url) return null;

    try {
        const apiUrl = `https://api.microlink.io/?url=${encodeURIComponent(url)}&screenshot=false&meta=false&embed=screenshot.url`;
        const response = await fetch(apiUrl);
        if (!response.ok) {
            throw new Error(`Preview API failed: ${response.status}`);
        }

        const payload = await response.json();
        const data = payload?.data || {};

        return {
            title: data.title || title,
            imageUrl: data.image?.url || fallbackThumbnail || "",
            domain: data.publisher || "",
            destinationUrl: url,
        };
    } catch {
        return {
            title,
            imageUrl: fallbackThumbnail || "",
            domain: "",
            destinationUrl: url,
        };
    }
}

export default function WorkCard({ year, company, title, description, tags = [], link, applicationLink, previews = [], fallbackThumbnail = "" }) {
    const [previewItems, setPreviewItems] = useState([]);
    const descriptionText = description
        ? description.length > 370
            ? `${description.slice(0, 370).trimEnd()}...`
            : description
        : "";

    useEffect(() => {
        let isMounted = true;

        async function fetchPreviews() {
            const suppliedPreviews = Array.isArray(previews) ? previews.filter((item) => item?.url) : [];
            const urls = [link, applicationLink].filter(Boolean);

            if (suppliedPreviews.length > 0) {
                if (isMounted) {
                    setPreviewItems(suppliedPreviews.slice(0, 2).map((item) => ({
                        title: item.title || title,
                        imageUrl: item.thumbnail || fallbackThumbnail || "",
                        domain: item.domain || "",
                        destinationUrl: item.url,
                    })));
                }
                return;
            }

            const fetched = await Promise.all(urls.map((url) => fetchPreviewForUrl(url, title, fallbackThumbnail)));
            const valid = fetched.filter(Boolean);

            if (isMounted) {
                setPreviewItems(valid.slice(0, 2));
            }
        }

        fetchPreviews();

        return () => {
            isMounted = false;
        };
    }, [applicationLink, fallbackThumbnail, link, previews, title]);

    const Inner = (
        <article className="card p-6 md:p-8 flex flex-col">
            <div className="flex-1">
                <div className="flex items-baseline justify-between mb-4">
                    {year && <span className="font-mono text-[12px] text-fg-soft">{year}</span>}
                    {company && <span className="text-[12px] text-fg-muted">{company}</span>}
                </div>

                <h3 className="font-display text-[20px] md:text-[22px] leading-snug tracking-tight text-fg">{title}</h3>

                {descriptionText && (
                    <div className="mt-3">
                        <p className="text-[14px] text-fg-muted leading-relaxed overflow-hidden">{descriptionText}</p>
                    </div>
                )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                {tags.map((t) => (
                    <span key={t} className="pill">{t}</span>
                ))}
            </div>

            {previewItems.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                    {previewItems.map((preview, index) => (
                        <div key={`${preview.destinationUrl || index}-${index}`}>
                            <PreviewCard
                                imageUrl={preview.imageUrl}
                                title={preview.title || title}
                                domain={preview.domain}
                                destinationUrl={preview.destinationUrl}
                            />
                        </div>
                    ))}
                </div>
            )}
        </article>
    );

    return Inner;
}